import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PaymentMethod } from '@stripe/stripe-js';

import { RequestState } from 'ds/types';
import { BuildingInstruction, BuildingUnitInstruction, BuildingUnitTenancy, BuildingUser } from 'shared';
import {
  AccessPath,
  InsuranceRequirementsDocument,
  TenantCoi,
  TenantFloorplan,
  TenantLease
} from 'ux/Tenant/TenantAdmin/Building/types';
import { TenancyService } from 'ux/Tenant/TenantAdmin/types';

import { BillingContact, BuildingUnit, Member, Payment, PlatformFee } from './types';

type TenancyState = {
  billing_contacts: BillingContact[] | null | undefined;
  services_payment_method: PaymentMethod | null | undefined;
  rent_payment_method: PaymentMethod | null | undefined;
  building_units: BuildingUnit[];
  members: Member[];
  payments: Payment[];
  failed_payments: Payment[] | null;
  platform_fees: PlatformFee[] | null;
  sales_tax_rate: number | null;
  tenancy_services: TenancyService[] | null;
  building_unit_tenancies: BuildingUnitTenancy[] | null;
  payment_methods: PaymentMethod[] | null;
  tenancy_leases: TenantLease[] | null;
  tenancy_cois: TenantCoi[] | null;
  tenancy_floorplans: TenantFloorplan[] | null;
  tenancy_insurance_requirements_documents: InsuranceRequirementsDocument[] | null;
  tenancyDocumentsRequestState: RequestState;
  access_paths: AccessPath[] | null;
};

const initialState: TenancyState = {
  billing_contacts: undefined,
  services_payment_method: null,
  rent_payment_method: null,
  tenancyDocumentsRequestState: 'ready',
  building_units: [],
  members: [],
  payments: [],
  failed_payments: null,
  platform_fees: null,
  sales_tax_rate: null,
  tenancy_services: null,
  building_unit_tenancies: null,
  payment_methods: null,
  tenancy_leases: null,
  tenancy_cois: null,
  tenancy_floorplans: null,
  tenancy_insurance_requirements_documents: null,
  access_paths: null
};

const tenancySlice = createSlice({
  name: 'tenancy',
  initialState,
  reducers: {
    setBillingContacts: (state, action: PayloadAction<BillingContact[] | null | undefined>) => {
      state.billing_contacts = action.payload;
    },
    addBillingContact: (state, action: PayloadAction<BillingContact>) => {
      if (!state.billing_contacts) {
        state.billing_contacts = [action.payload];
        return;
      }

      const existingIndex = state.billing_contacts.findIndex(contact => contact.id === action.payload.id);

      if (existingIndex !== -1) {
        state.billing_contacts[existingIndex] = action.payload;
        return;
      }

      state.billing_contacts.push(action.payload);
    },
    updateBillingContact: (state, action: PayloadAction<BillingContact>) => {
      if (!state.billing_contacts) {
        state.billing_contacts = [action.payload];
        return;
      }

      const index = state.billing_contacts.findIndex(contact => contact.id === action.payload.id);

      if (index === -1) {
        state.billing_contacts.push(action.payload);
        return;
      }

      state.billing_contacts[index] = action.payload;
    },
    removeBillingContact: (state, action: PayloadAction<number>) => {
      if (!state.billing_contacts) {
        return;
      }

      state.billing_contacts = state.billing_contacts.filter(contact => contact.id !== action.payload);
    },
    setServicesPaymentMethod: (state, action: PayloadAction<PaymentMethod | null | undefined>) => {
      const services_payment_method = action.payload;
      state.services_payment_method = services_payment_method;
    },
    updateServicesPaymentMethod: (state, action: PayloadAction<Partial<PaymentMethod>>) => {
      if (state.services_payment_method) {
        state.services_payment_method = {
          ...state.services_payment_method,
          ...action.payload
        };
      }
    },
    setRentPaymentMethod: (state, action: PayloadAction<PaymentMethod | null | undefined>) => {
      const rent_payment_method = action.payload;
      state.rent_payment_method = rent_payment_method;
    },
    setBuildingUnits: (state, action: PayloadAction<BuildingUnit[]>) => {
      const building_units = action.payload;
      state.building_units = building_units;
    },
    setMembers: (state, action: PayloadAction<Member[]>) => {
      state.members = action.payload;
    },
    addMember: (state, action: PayloadAction<Member>) => {
      state.members.push(action.payload);
    },
    updateMember: (state, action: PayloadAction<Partial<Member> & { id: number }>) => {
      const index = state.members.findIndex(member => member.id === action.payload.id);
      if (index !== -1) {
        state.members[index] = { ...state.members[index], ...action.payload };
      }
    },
    removeMember: (state, action: PayloadAction<{ id: number }>) => {
      state.members = state.members.filter(member => member.id !== action.payload.id);
    },
    setPayments: (state, action: PayloadAction<Payment[]>) => {
      const payments = action.payload;
      state.payments = payments;
    },
    setPlatformFees: (state, action: PayloadAction<PlatformFee[]>) => {
      const platform_fees = action.payload;
      state.platform_fees = platform_fees;
    },
    updatePlatformFee: (state, action: PayloadAction<Partial<PlatformFee> & { id: number }>) => {
      if (!state.platform_fees) {
        return;
      }

      const index = state.platform_fees.findIndex(platform_fee => platform_fee.id === action.payload.id);
      if (index !== -1) {
        state.platform_fees[index] = { ...state.platform_fees[index], ...action.payload };
      }
    },
    setSalesTaxRate: (state, action: PayloadAction<number | null>) => {
      state.sales_tax_rate = action.payload;
    },
    setTenancyServices: (state, action: PayloadAction<TenancyService[]>) => {
      state.tenancy_services = action.payload;
    },
    setBuildingUnitTenancies: (state, action: PayloadAction<BuildingUnitTenancy[]>) => {
      state.building_unit_tenancies = action.payload;
    },
    updateBuildingUnitTenancy: (state, action: PayloadAction<Partial<BuildingUnitTenancy> & { id: number }>) => {
      if (!state.building_unit_tenancies) {
        return;
      }
      const index = state.building_unit_tenancies.findIndex(
        building_unit_tenancy => building_unit_tenancy.id === action.payload.id
      );
      if (index !== -1) {
        state.building_unit_tenancies[index] = { ...state.building_unit_tenancies[index], ...action.payload };
      }
    },
    setPaymentMethods: (state, action: PayloadAction<PaymentMethod[]>) => {
      state.payment_methods = action.payload;
    },
    setTenancyLeases: (state, action: PayloadAction<TenantLease[] | null>) => {
      state.tenancy_leases = action.payload;
    },
    addTenancyLease: (state, action: PayloadAction<TenantLease>) => {
      state.tenancy_leases = [action.payload, ...(state.tenancy_leases || [])];
    },
    setTenancyCois: (state, action: PayloadAction<TenantCoi[] | null>) => {
      state.tenancy_cois = action.payload;
    },
    addTenancyCoi: (state, action: PayloadAction<TenantCoi>) => {
      state.tenancy_cois = [action.payload, ...(state.tenancy_cois || [])];
    },
    removeTenancyCoi: (state, action: PayloadAction<number>) => {
      if (!state.tenancy_cois) {
        return;
      }
      state.tenancy_cois = state.tenancy_cois.filter(coi => coi.id !== action.payload);
    },
    setTenancyFloorplans: (state, action: PayloadAction<TenantFloorplan[] | null>) => {
      state.tenancy_floorplans = action.payload;
    },
    addTenancyFloorplan: (state, action: PayloadAction<TenantFloorplan>) => {
      state.tenancy_floorplans = [action.payload, ...(state.tenancy_floorplans || [])];
    },
    removeTenancyFloorplan: (state, action: PayloadAction<number>) => {
      if (!state.tenancy_floorplans) {
        return;
      }
      state.tenancy_floorplans = state.tenancy_floorplans.filter(floorplan => floorplan.id !== action.payload);
    },
    setTenancyInsuranceRequirementsDocuments: (
      state,
      action: PayloadAction<InsuranceRequirementsDocument[] | null>
    ) => {
      state.tenancy_insurance_requirements_documents = action.payload;
    },
    addTenancyInsuranceRequirementsDocument: (state, action: PayloadAction<InsuranceRequirementsDocument>) => {
      state.tenancy_insurance_requirements_documents = [
        action.payload,
        ...(state.tenancy_insurance_requirements_documents || [])
      ];
    },
    removeTenancyInsuranceRequirementsDocument: (state, action: PayloadAction<number>) => {
      if (!state.tenancy_insurance_requirements_documents) {
        return;
      }
      state.tenancy_insurance_requirements_documents = state.tenancy_insurance_requirements_documents.filter(
        doc => doc.id !== action.payload
      );
    },
    setFailedPayments: (state, action: PayloadAction<Payment[] | null>) => {
      state.failed_payments = action.payload;
    },
    setTenancyDocumentsRequestState: (state, action: PayloadAction<RequestState>) => {
      state.tenancyDocumentsRequestState = action.payload;
    },
    setAccessPaths: (state, action: PayloadAction<AccessPath[] | null>) => {
      state.access_paths = action.payload;
    },
    addBuildingInstruction: (state, action: PayloadAction<BuildingInstruction>) => {
      state.building_units.forEach(unit => {
        if (!unit.building_instructions) {
          unit.building_instructions = [action.payload];
          return;
        }

        const exists = unit.building_instructions.some(instruction => instruction.id === action.payload.id);
        if (!exists) {
          unit.building_instructions.push(action.payload);
        }
      });
    },
    updateBuildingInstructionValue: (
      state,
      action: PayloadAction<{ instructionId: BuildingInstruction['id']; value: string }>
    ) => {
      const { instructionId, value } = action.payload;

      state.building_units.forEach(unit => {
        if (!unit.building_instructions) return;
        unit.building_instructions = unit.building_instructions.map(instruction =>
          instruction.id === instructionId ? { ...instruction, value } : instruction
        );
      });
    },
    removeBuildingInstruction: (state, action: PayloadAction<BuildingInstruction['id']>) => {
      state.building_units.forEach(unit => {
        if (!unit.building_instructions) return;
        unit.building_instructions = unit.building_instructions.filter(
          instruction => instruction.id !== action.payload
        );
      });
    },
    addBuildingUnitInstruction: (state, action: PayloadAction<BuildingUnitInstruction>) => {
      const targetUnitId = action.payload.building_unit_id;

      state.building_units.forEach(unit => {
        if (targetUnitId && unit.id !== targetUnitId) {
          return;
        }

        if (!unit.building_unit_instructions) {
          unit.building_unit_instructions = [action.payload];
          return;
        }

        const exists = unit.building_unit_instructions.some(instruction => instruction.id === action.payload.id);
        if (!exists) {
          unit.building_unit_instructions.push(action.payload);
        }
      });
    },
    updateBuildingUnitInstructionValue: (
      state,
      action: PayloadAction<{ instructionId: NonNullable<BuildingUnitInstruction['id']>; value: string }>
    ) => {
      const { instructionId, value } = action.payload;

      state.building_units.forEach(unit => {
        if (!unit.building_unit_instructions) return;
        unit.building_unit_instructions = unit.building_unit_instructions.map(instruction =>
          instruction.id === instructionId ? { ...instruction, value } : instruction
        );
      });
    },
    removeBuildingUnitInstruction: (state, action: PayloadAction<NonNullable<BuildingUnitInstruction['id']>>) => {
      state.building_units.forEach(unit => {
        if (!unit.building_unit_instructions) return;
        unit.building_unit_instructions = unit.building_unit_instructions.filter(
          instruction => instruction.id !== action.payload
        );
      });
    },
    addBuildingContact: (state, action: PayloadAction<{ buildingId: number; buildingUser: BuildingUser }>) => {
      const { buildingId, buildingUser } = action.payload;

      state.building_units.forEach(unit => {
        if (unit.building_id !== buildingId) {
          return;
        }

        if (!unit.building_users) {
          unit.building_users = [buildingUser];
          return;
        }

        const exists = unit.building_users.some(user => user.id === buildingUser.id);
        if (!exists) {
          unit.building_users.push(buildingUser);
        }
      });
    }
  }
});

export const { actions } = tenancySlice;
export default tenancySlice.reducer;
